import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { AlertListRow } from 'components/ui/AlertListRow/AlertListRow'
import { tokens } from 'styling/tokens'
import { AttendanceDonut } from './AttendanceDonut'
import {
  BarRow,
  BarLabel,
  BarTrack,
  BarFill,
  BarValue,
  OccupancyValue,
  OccupancyCaption,
} from '../hrLayout.styled'

const DEPARTMENTS = [
  { name: 'Mining Ops', present: 78, total: 85 },
  { name: 'Maintenance', present: 39, total: 42 },
  { name: 'Haulage', present: 30, total: 35 },
  { name: 'Processing', present: 27, total: 28 },
  { name: 'HSE', present: 14, total: 15 },
  { name: 'Admin', present: 10, total: 10 },
]

const ALERTS = [
  { title: '3 contracts expiring', meta: 'This week', status: 'danger', tag: 'Action' },
  { title: '5 timesheets missing', meta: 'Yesterday', status: 'warning', tag: 'Review' },
  { title: '2 accommodation requests', meta: 'Today', status: 'info', tag: 'New' },
  { title: 'Overtime approval pending', meta: '2 days ago', status: 'warning', tag: 'Review' },
  { title: 'New hire onboarding — 3 staff', meta: 'Today', status: 'info', tag: 'New' },
  { title: 'Safety incident report', meta: '3 days ago', status: 'danger', tag: 'Action' },
  { title: 'Visa renewal — 4 staff', meta: 'This month', status: 'warning', tag: 'Review' },
  { title: 'Medical check due', meta: 'Next week', status: 'info', tag: 'New' },
] as const

const MEALS = [
  { name: 'Breakfast', served: 172, total: 180, color: tokens.status.success.fg },
  { name: 'Lunch', served: 142, total: 195, color: tokens.accent.base },
  { name: 'Dinner', served: 0, total: 190, color: tokens.text.muted },
]

const TRANSPORT = [
  { label: 'Inbound', count: 78, total: 85, color: tokens.status.info.fg },
  { label: 'Outbound', count: 54, total: 72, color: tokens.status.success.fg },
  { label: 'North gate', count: 22, total: 30, color: tokens.accent.base },
]

const SUGGESTIONS = [
  'Move 2 staff from Admin to Haulage to balance load',
  'Approve 3 pending overtime requests before payroll',
  'Accommodation at 84% — pre-book 12 beds for next intake',
  'Renew 4 visas expiring this month',
]

const FALLBACK_NOTIFS = [
  { id: 'n1', message: 'Leave request — M. Jovanović', meta: '2m ago' },
  { id: 'n2', message: 'Payroll exported', meta: '1h ago' },
  { id: 'n3', message: 'Shift swap pending', meta: '3h ago' },
  { id: 'n4', message: 'Contract signed — A. Petrović', meta: '5h ago' },
]

export const HrWidgetBody = ({ type }: { type: string }) => {
  const { accommodationSummary, shiftsSummary, notifications } = useSelector(
    (state: RootState) => state.hrDashboard,
  )

  const accommodation = useMemo(() => {
    const a = accommodationSummary
    const total = a?.totalCapacity ?? 220
    const occupied = a?.occupied ?? 184
    const available2 = a?.available ?? total - occupied
    const rawRate = a?.occupancyRate
    const pct =
      rawRate != null
        ? Math.round(rawRate <= 1 ? rawRate * 100 : rawRate)
        : total
          ? Math.round((occupied / total) * 100)
          : 0
    return { total, occupied, available: available2, pct, dirty: a?.dirtyRooms ?? 0 }
  }, [accommodationSummary])

  const shifts = useMemo(() => {
    const list = shiftsSummary?.shifts ?? []
    const source = list.length
      ? list.map((s) => {
          const raw = s.color?.trim()
          const color = raw
            ? raw.startsWith('#')
              ? raw
              : `#${raw}`
            : tokens.accent.base
          return { name: s.name, count: s.numberOfUsers ?? 0, color }
        })
      : [
          { name: 'Day shift', count: 84, color: tokens.accent.base },
          { name: 'Evening shift', count: 38, color: tokens.status.info.fg },
          { name: 'Night shift', count: 20, color: tokens.status.success.fg },
        ]
    const max = Math.max(...source.map((s) => s.count), 1)
    const rows = source.map((s) => ({ ...s, pct: Math.round((s.count / max) * 100) }))
    return { rows }
  }, [shiftsSummary])

  const notifs = useMemo(() => {
    const data = notifications?.data ?? []
    if (!data.length) return FALLBACK_NOTIFS
    return data.slice(0, 8).map((n: { id?: string | number; message?: string }, i: number) => ({
      id: String(n.id ?? i),
      message: n.message ?? 'Notification',
      meta: 'recently',
    }))
  }, [notifications])

  if (type === 'attendance') {
    return <AttendanceDonut />
  }
  if (type === 'department') {
    return (
      <>
        {DEPARTMENTS.map((d) => {
          const pct = Math.round((d.present / d.total) * 100)
          return (
            <BarRow key={d.name}>
              <BarLabel>{d.name}</BarLabel>
              <BarTrack>
                <BarFill $pct={pct} $color={pct >= 95 ? tokens.status.success.fg : undefined} />
              </BarTrack>
              <BarValue>{pct}%</BarValue>
            </BarRow>
          )
        })}
      </>
    )
  }
  if (type === 'accommodation') {
    return (
      <>
        <OccupancyValue>
          {accommodation.occupied} / {accommodation.total}
        </OccupancyValue>
        <OccupancyCaption>beds occupied · {accommodation.pct}% occupancy</OccupancyCaption>
        <BarTrack>
          <BarFill $pct={accommodation.pct} />
        </BarTrack>
        <OccupancyCaption>
          {accommodation.available} beds available · {accommodation.dirty} dirty rooms
        </OccupancyCaption>
      </>
    )
  }
  if (type === 'shifts') {
    return (
      <>
        {shifts.rows.map((s) => (
          <BarRow key={s.name}>
            <BarLabel>{s.name}</BarLabel>
            <BarTrack>
              <BarFill $pct={s.pct} $color={s.color} />
            </BarTrack>
            <BarValue>{s.count}</BarValue>
          </BarRow>
        ))}
      </>
    )
  }
  if (type === 'notifications') {
    return (
      <>
        {notifs.map((n) => (
          <AlertListRow key={n.id} title={n.message} meta={n.meta} />
        ))}
      </>
    )
  }
  if (type === 'alerts') {
    return (
      <>
        {ALERTS.map((a) => (
          <AlertListRow key={a.title} title={a.title} meta={a.meta} pillLabel={a.tag} pillTone={a.status} />
        ))}
      </>
    )
  }
  if (type === 'food') {
    return (
      <>
        {MEALS.map((m) => {
          const pct = m.total ? Math.round((m.served / m.total) * 100) : 0
          return (
            <BarRow key={m.name}>
              <BarLabel>{m.name}</BarLabel>
              <BarTrack>
                <BarFill $pct={pct} $color={m.color} />
              </BarTrack>
              <BarValue>{m.served}</BarValue>
            </BarRow>
          )
        })}
      </>
    )
  }
  if (type === 'transport') {
    return (
      <>
        {TRANSPORT.map((t) => {
          const pct = t.total ? Math.round((t.count / t.total) * 100) : 0
          return (
            <BarRow key={t.label}>
              <BarLabel>{t.label}</BarLabel>
              <BarTrack>
                <BarFill $pct={pct} $color={t.color} />
              </BarTrack>
              <BarValue>{t.count}</BarValue>
            </BarRow>
          )
        })}
      </>
    )
  }
  if (type === 'suggestions') {
    return (
      <>
        {SUGGESTIONS.map((s) => (
          <AlertListRow key={s} title={s} />
        ))}
      </>
    )
  }
  return null
}
